import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Input } from '@cokitchen/cokitchen-components';
import MealCategory from 'components/meal-category';
import { Bars } from 'react-loader-spinner';
import useOrdersContext from 'contexts/orders.context';
import RestaurantHeader from 'components/restuarant-header';
import { RatingFilledIcon } from 'cokitchen-icons';
import useDashboardContext from 'contexts/dashboard.context';
import { ROUTE_KEYS } from 'utils/constants';
import { ENUM_ORDERS_TYPE } from 'utils/enum';
import { calculateReactivationTime, getIsBusy, isBrandOpenNow } from 'utils/functions';
import { MealCategoryType } from 'models/meal-category.model';
import { MealType } from 'models/meal.model';

const Brand = () => {
  const mounting = useRef(true);
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    restaurant,
    restaurantLoading,
    loadRestaurant,
    handleReset,
    bulkOrderMeals,
    batchOrderMeals,
    calculatedOrder
  } = useOrdersContext();
  const [activecategory, setActiveCategory] = useState<MealCategoryType | null>(null);
  const [search, setSearch] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [reactivationTime, setReactivationTime] = useState<number>();
  const { setIsBackLink, setBackLinkTitle, setBackLink } = useDashboardContext();
  const [searchParams] = useSearchParams();
  const [isBrandOpen, setIsBrandOpen] = useState(false);

  const orderType = useMemo(() => {
    return (searchParams.get('type') as ENUM_ORDERS_TYPE) ?? '';
  }, [searchParams]);

  useEffect(() => {
    if (restaurant) {
      if (orderType === ENUM_ORDERS_TYPE.BATCH) {
        setIsBrandOpen(true);
      } else {
        setIsBrandOpen(isBrandOpenNow(restaurant));
      }
    }
  }, [restaurant]);

  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle('Return to restaurants');
    setBackLink(
      orderType === ENUM_ORDERS_TYPE.BATCH
        ? '/orders/batched/brands'
        : `orders/restaurants?type=${orderType}`
    );

    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
      setBackLink('');
    };
  }, []);

  const getOrderCount = (meal: MealType) => {
    const orderHandlers = {
      [ENUM_ORDERS_TYPE.BULK]: () =>
        bulkOrderMeals.filter((_m) => _m.id === meal.id).reduce((a, b) => a + b.quantity, 0),

      [ENUM_ORDERS_TYPE.BATCH]: () =>
        batchOrderMeals.filter((_m) => _m.id === meal.id).reduce((a, b) => a + b.quantity, 0),

      [ENUM_ORDERS_TYPE.GROUP]: () =>
        calculatedOrder?.meals
          ?.map((_m) => _m.meals)
          .flat()
          ?.filter((_m) => _m.id === meal.id)
          .reduce((a, b) => a + b.quantity, 0)
    };

    return orderHandlers[orderType]?.() ?? 0;
  };

  const categoriesWithMealCounts = useMemo(() => {
    return restaurant?.meal_category?.map((cat) => {
      const newCat = { ...cat };
      const meals = newCat.meals.filter((m) =>
        search
          ? m?.name?.toLowerCase().includes(search.toLowerCase()) ||
            m?.description?.toLowerCase().includes(search.toLowerCase())
          : true
      );
      newCat.meals = meals.map((m) => ({
        ...m,
        orderCount: getOrderCount(m) ?? 0
      }));
      return newCat;
    });
  }, [restaurant, search, calculatedOrder]);

  const setDebouncedSearch = (value: string) => {
    setTimeout(() => {
      setSearch(value);
    }, 500);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (mounting.current) {
      loadRestaurant(id);
    }
    if (
      !orderType ||
      ![ENUM_ORDERS_TYPE.GROUP, ENUM_ORDERS_TYPE.BULK, ENUM_ORDERS_TYPE.BATCH].includes(orderType)
    ) {
      handleReset();
      navigate(`/${ROUTE_KEYS.ORDERS}`);
    }
    return () => {
      mounting.current = false;
    };
  }, [id]);

  useEffect(() => {
    if (restaurant) {
      if (restaurant.meal_categories?.length && restaurant.meal_category)
        setActiveCategory(restaurant.meal_category?.[0]);
      const categoryMeals = document.querySelectorAll('.meal_container');

      const handleIntersection = (entries: IntersectionObserverEntry[]) => {
        entries.map((entry) => {
          if (entry.isIntersecting) {
            const category = entry.target
              .querySelector('p')
              ?.classList[0].replace('category_header', '');
            const categoryElement = document.querySelector(`.meal_category_${category}`);
            const activeCategory = document.querySelector('.active');
            const meal_category_id = entry.target.getAttribute('data-key');
            const activeCategoryObject = restaurant.meal_category?.find(
              (category) => category.id === meal_category_id
            );
            setActiveCategory(activeCategoryObject ?? null);
            if (activeCategory) {
              activeCategory.classList.remove('active');
            }
            if (categoryElement) {
              categoryElement.classList.add('active');
            }
          }
        });
      };
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.5
      };
      const observer = new IntersectionObserver(handleIntersection, options);
      categoryMeals.forEach((meal) => {
        observer.observe(meal);
      });
    }
  }, [restaurant]);

  const handleCategoryClick = (categoryId: string) => {
    const bodyScroll = document.querySelector('.body_main');
    const categoryHeader =
      document.querySelector(`.category_header${categoryId}`) || (null as HTMLElement | null);

    const categoryElement = document.querySelector(
      `.meal_category_${categoryId}`
    ) as HTMLElement | null;

    const activeCategory = document.querySelector('.active');
    const activeCategoryObject = restaurant?.meal_category?.find(
      (category) => category.id === categoryId
    );
    setActiveCategory(activeCategoryObject ?? null);
    if (activeCategory) {
      activeCategory.classList.remove('active');
    }
    if (categoryElement) {
      categoryElement.classList.add('active');
    }
    if (categoryHeader) {
      const elementPosition = categoryHeader.getBoundingClientRect()?.top + window.pageYOffset;

      // Scroll to the position of the element
      if (categoryHeader && bodyScroll) {
        window.scrollTo({
          top: (elementPosition || 0) - 150,
          behavior: 'smooth'
        });
      }
    }
  };

  useEffect(() => {
    if (restaurant) {
      const restaurantIsBusy = getIsBusy(restaurant);
      setIsBusy(restaurantIsBusy);
      setReactivationTime(calculateReactivationTime(restaurant.reactivation_time));
    }
  }, [restaurant]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (restaurant) {
        const time = calculateReactivationTime(restaurant.reactivation_time);
        setReactivationTime(time);
      }
    }, 1000);
    return () => clearInterval(intervalId);
  }, [restaurant?.reactivation_time]);

  useEffect(() => {
    if (reactivationTime === 0 && restaurant) {
      setIsBusy(getIsBusy(restaurant));
    }
  }, [reactivationTime]);

  return (
    <>
      {restaurantLoading ? (
        <div className='grow w-full h-screen flex justify-center pt-[25vh]'>
          <Bars color='#E60A2B' height={50} width={50} />
        </div>
      ) : (
        <div className='relative min-h-screen'>
          <RestaurantHeader
            image={restaurant?.images[0]}
            logo={restaurant?.logo}
            isBusy={isBusy}
            reactivationTime={reactivationTime}
            isBrandOpen={isBrandOpen}
          />
          <div className='w-full flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-4 lg:gap-8 lg:px-10 pt-10 sm:px-3 mt-2'>
            <div>
              <h3 className='text-[24px] leading-[28.8px] font-semibold'>{restaurant?.name}</h3>
              <p className='text-gray-9'>{restaurant?.description}</p>
              <div className='flex gap-1 text-[#EEB507] items-center'>
                <RatingFilledIcon />
                <p className='text-gray-9'> 5.0 Excellent (1315+ orders)</p>
              </div>
            </div>
            <Input
              type='search'
              placeholder='Search menu items'
              onChange={(e) => setDebouncedSearch(e.target.value)}
              className='max-w-[292px]'
              inputClass='h-[36px] rounded-md border-[1px] border-[#E5E5E5] text-sm'
            />
          </div>

          <div className='w-full sticky z-[10000] top-[79px] bg-white mt-5 mb-3 border-t border-t-[#EDEDEE] border-b border-b-[#EDEDEE] py-[14px] sm:px-3 lg:pl-10'>
            <div className='w-full mx-auto'>
              <div className='flex gap-2 overflow-scroll no-scrollbar'>
                {restaurant?.meal_category?.map((category) => (
                  <div
                    className={`cursor-pointer px-2 py-1 rounded-lg w-fit ${
                      activecategory?.id === category.id ? 'bg-[#FEF0F2]' : 'bg-transparent'
                    }`}
                    key={category.id}
                    onClick={() => handleCategoryClick(category.id)}>
                    <p
                      className={`text-sm whitespace-nowrap text-[#96999C]
                    ${activecategory?.id === category.id ? 'text-red-5' : ''}
                    `}>
                      {category.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className='w-full'>
            <div className='w-full mx-auto flex flex-col gap-6 px-2 py-3 sm:px-3 lg:px-10'>
              {categoriesWithMealCounts?.map((category) => {
                return (
                  <MealCategory
                    {...category}
                    key={category.id}
                    active={category.id === activecategory?.id}
                    orderType={orderType}
                    isBusy={isBusy}
                    isBrandOpen={isBrandOpen}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Brand;
